import { useRef, useState, useEffect, useCallback } from "react";

import {
    Box,
    Card,
    CardActions,
    CardContent,
    IconButton,
    styled,
} from "@mui/material";
import FastForward from "@mui/icons-material/FastForward";
import FastRewind from "@mui/icons-material/FastRewind";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";

import { DateTime } from "luxon";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { playbackActions } from "./store/slice";
import { playbackSessionSelectors } from "./store/selectors.session";
import { selectShowSidebar } from "features/core/store/selectors";

import { PlaybackTimeline, PlaybackTimelineRef } from "./PlaybackTimeline";

import { useAnimationFrameTicker } from "hooks/useAnimationFrameTicker";
import { PlaybackConfiguration } from "types/AppConfig";
import CurrentTime from "./CurrentTime";

const IconButtonWhite = styled(IconButton)(({ theme }) => ({
    color: theme.palette.common.white,
    ":disabled": {
        color: theme.palette.common.white,
        opacity: "30%",
    },
}));

export interface PlaybackControlProps {
    startDateTime: string;
    endDateTime: string;
    playbackConfiguration: PlaybackConfiguration;
    drawerWidth: number;
    displayAsFloatingFooter: boolean;
}

export function PlaybackControls(props: PlaybackControlProps) {
    const dispatch = useAppDispatch();

    const userRequestedPlayMode = useAppSelector(
        playbackSessionSelectors.selectUserRequestedPlayMode,
    );

    const showSidebar = useAppSelector(selectShowSidebar);

    // ticker
    const onTick = useCallback(
        (elapsedPlayTimeInMs: number) => {
            dispatch(
                playbackActions.setElapsedPlayTimeInMs({
                    elapsedPlayTimeInMs,
                }),
            );
        },
        [dispatch],
    );

    const { startTicker, stopTicker, restartTicker, isTicking } =
        useAnimationFrameTicker(
            props.playbackConfiguration.animationFrameTickerTickEveryInMs,
            onTick,
        );

    const playbackPlaying = useAppSelector(
        playbackSessionSelectors.selectPlaybackPlaying,
    );

    useEffect(() => {
        const isNowTicking = isTicking();

        if (playbackPlaying && !isNowTicking) {
            startTicker();
        } else if (!playbackPlaying && isNowTicking) {
            if (userRequestedPlayMode === "play") {
                // save current playback position to enable playback from the current position when resumed after buffering
                dispatch(playbackActions.saveCurrentPlaybackPosition());
            }

            stopTicker();
        }
    }, [
        startTicker,
        stopTicker,
        playbackPlaying,
        isTicking,
        userRequestedPlayMode,
        dispatch,
    ]);

    // timeline
    const onAddToOrSubtractFromPlaybackDateTime = (
        addOrSubstract: "add" | "subtract",
        seconds: number,
    ) => {
        switch (addOrSubstract) {
            case "add":
                dispatch(
                    playbackActions.addSecondsToPlaybackStartDateTime({
                        seconds,
                    }),
                );
                break;
            case "subtract":
                dispatch(
                    playbackActions.subtractSecondsFromPlaybackStartDateTime({
                        seconds,
                    }),
                );
                break;
        }

        restartTicker();
    };

    const onSetPlaybackDateTime = (dateTime: DateTime) => {
        dispatch(
            playbackActions.setPlaybackStartDateTime({
                playbackStartDateTime: dateTime.toISO() as string,
            }),
        );

        restartTicker();
    };

    const userRequestedPlayModePlay =
        userRequestedPlayMode === "play" || userRequestedPlayMode === "resume";

    // when controls float we need to add bottom spacing to make sure user can see all recordings properly
    const cardRef = useRef<HTMLDivElement | null>(null);
    const playbackTimeLineRef = useRef<PlaybackTimelineRef | null>(null);
    const [playbackControlsHeight, setPlaybackControlsHeight] = useState(0);
    const [showFullSize, setShowFullSize] = useState(true);

    const mouseLeaveTimeOut = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (cardRef.current) {
            cardRef.current?.addEventListener("transitionend", () => {
                if (playbackTimeLineRef.current) {
                    playbackTimeLineRef.current.redrawTimeline();
                }
            });
        }
    }, []);

    return (
        <>
            {props.displayAsFloatingFooter && (
                <Box
                    sx={(theme) => ({
                        height: `calc(${playbackControlsHeight}px + ${theme.spacing(5)})`,
                    })}
                />
            )}
            <Box
                sx={(theme) => ({
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    ...(!props.displayAsFloatingFooter && {
                        marginTop: theme.spacing(2),
                    }),
                    ...(props.displayAsFloatingFooter && {
                        position: "fixed",
                        bottom: theme.spacing(2),
                    }),
                })}
            >
                <Card
                    onMouseEnter={() => {
                        if (mouseLeaveTimeOut.current)
                            clearTimeout(mouseLeaveTimeOut.current);
                        setShowFullSize(true);
                    }}
                    onMouseLeave={() => {
                        if (mouseLeaveTimeOut.current)
                            clearTimeout(mouseLeaveTimeOut.current);

                        mouseLeaveTimeOut.current = setTimeout(() => {
                            setShowFullSize(false);
                        }, 5000);
                    }}
                    ref={cardRef}
                    elevation={5}
                    sx={(theme) => ({
                        backgroundColor: "black",
                        ...(props.displayAsFloatingFooter && {
                            marginLeft: showFullSize
                                ? `calc( ${showSidebar ? -props.drawerWidth : 0}px - ${theme.spacing(6)})`
                                : "-15%",
                            height: showFullSize ? "unset" : "50px",
                            width: showFullSize
                                ? `calc(100% - ${showSidebar ? props.drawerWidth : 0}px - ${theme.spacing(6)})`
                                : "10%",
                        }),
                        transition: theme.transitions.create(
                            ["height", "width", "marginLeft"],
                            {
                                easing: theme.transitions.easing.easeInOut,
                                duration: theme.transitions.duration.shorter,
                            },
                        ),
                    })}
                >
                    <CardActions
                        sx={{
                            justifyContent: "center",
                        }}
                    >
                        <IconButtonWhite
                            onClick={() =>
                                onAddToOrSubtractFromPlaybackDateTime(
                                    "subtract",
                                    30,
                                )
                            }
                            aria-label="Rewind 30 seconds"
                        >
                            <FastRewind />
                        </IconButtonWhite>
                        {!userRequestedPlayModePlay && (
                            <IconButtonWhite
                                onClick={() =>
                                    dispatch(playbackActions.startPlayback())
                                }
                                aria-label="Play"
                            >
                                <PlayArrow />
                            </IconButtonWhite>
                        )}

                        {userRequestedPlayModePlay && (
                            <IconButtonWhite
                                onClick={() =>
                                    dispatch(playbackActions.pausePlayback())
                                }
                                aria-label="Pause"
                            >
                                <Pause />
                            </IconButtonWhite>
                        )}

                        <IconButtonWhite
                            onClick={() =>
                                onAddToOrSubtractFromPlaybackDateTime("add", 30)
                            }
                            aria-label="Skip 30 seconds"
                        >
                            <FastForward />
                        </IconButtonWhite>
                        {showFullSize && <CurrentTime />}
                    </CardActions>
                    <CardContent
                        sx={{
                            paddingTop: 0,
                            visibility: showFullSize ? "hidden" : "visible",
                        }}
                    >
                        <PlaybackTimeline
                            ref={playbackTimeLineRef}
                            startDateTime={props.startDateTime}
                            endDateTime={props.endDateTime}
                            displayContinuousPlaybackWindow={
                                props.playbackConfiguration
                                    .enableContinuousPlaybackWindowFeature
                            }
                            onSetPlaybackDateTime={onSetPlaybackDateTime}
                            onTimelineGroupsChanged={() => {
                                if (cardRef.current) {
                                    setPlaybackControlsHeight(
                                        cardRef.current.getBoundingClientRect()
                                            .height,
                                    );
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </Box>
        </>
    );
}
